import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import { createCompany, fetchMyCompany } from "../api/companyWorkspace";
import CompanyPageHeader from "../ui/CompanyPageHeader";

const INITIAL = {
  name: "",
  city: "",
  description: "",
  website: "",
};

function CompanyCreateContent() {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const [gate, setGate] = useState("loading");
  const [values, setValues] = useState(INITIAL);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    fetchMyCompany()
      .then((data) => {
        if (requestIdRef.current !== requestId) return;
        if (data?.membership) {
          setGate("blocked");
        } else {
          setGate("ready");
        }
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        setFormError(messageFromError(err));
        setGate("error");
      });
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  const onChange = (name) => (event) => {
    const next = event.target.value;
    setValues((prev) => ({ ...prev, [name]: next }));
    setFieldErrors((prev) => {
      if (!prev?.[name]) return prev;
      const out = { ...prev };
      delete out[name];
      return out;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const body = {
        name: values.name.trim(),
        city: values.city.trim() || undefined,
        description: values.description.trim() || undefined,
        website: values.website.trim() || undefined,
      };
      const company = await createCompany(body);
      navigate(`/companies/${company.id}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setFormError(
          "У вас уже є поточне членство в іншій компанії. Автоматичний вихід не виконується — спочатку залиште або передайте поточну компанію.",
        );
      } else {
        const parsed = parseDrfFieldErrors(err, [
          "name",
          "city",
          "description",
          "website",
        ]);
        const { _form, ...fields } = parsed;
        setFieldErrors(fields);
        setFormError(_form || messageFromError(err));
      }
      setSaving(false);
    }
  };

  if (gate === "loading") {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Перевірка членства" />
      </div>
    );
  }

  if (gate === "blocked") {
    return (
      <section className="clients-page feature-company-workspace" id="Csection">
        <div className="container">
          <CompanyPageHeader title="Створити компанію" />
          <EmptyState
            icon="fa-solid fa-ban"
            title="Друга компанія недоступна"
            description="У V1 дозволено лише одне поточне членство. Спочатку вийдіть або передайте поточну компанію."
            size="md"
          />
          <Link to="/companies/mine" className="action-btn">
            До моєї компанії
          </Link>
        </div>
      </section>
    );
  }

  if (gate === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося перевірити членство"
          description={formError || "Спробуйте пізніше."}
          size="md"
        />
      </div>
    );
  }

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container feature-company-workspace__narrow">
        <CompanyPageHeader
          title="Створити компанію"
          subtitle="Після створення ви станете власником. Друге одночасне членство не підтримується."
        />
        <form className="ui-form" onSubmit={onSubmit} noValidate>
          <Field id="company-create-name" label="Назва" error={fieldErrors.name} required>
            <TextInput
              name="name"
              value={values.name}
              onChange={onChange("name")}
              required
              autoComplete="organization"
            />
          </Field>
          <Field id="company-create-city" label="Місто" error={fieldErrors.city}>
            <TextInput name="city" value={values.city} onChange={onChange("city")} />
          </Field>
          <Field
            id="company-create-website"
            label="Вебсайт"
            error={fieldErrors.website}
            help="Починайте з https://"
          >
            <TextInput
              name="website"
              type="url"
              value={values.website}
              onChange={onChange("website")}
            />
          </Field>
          <Field
            id="company-create-description"
            label="Опис"
            error={fieldErrors.description}
          >
            <Textarea
              name="description"
              value={values.description}
              onChange={onChange("description")}
              rows={4}
            />
          </Field>
          {formError ? <InlineError>{formError}</InlineError> : null}
          <div className="feature-company-workspace__inline-actions">
            <button type="submit" className="action-btn" disabled={saving}>
              {saving ? "Створення…" : "Створити"}
            </button>
            <Link to="/companies/mine" className="action-btn action-btn--ghost">
              Скасувати
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}

export default CompanyCreateContent;
